import { Injectable } from '@angular/core';
import { IonTabs, NavController, Platform } from '@ionic/angular';
import OneSignal from 'onesignal-cordova-plugin';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpFunctionResult } from '../../_interfaces/HttpFunctionResult';
import { TicketDetailPagePage } from '../../_pages/ticket-detail-page/ticket-detail-page.page';
import { NotificationStartComponent } from '../../_template/notification-start/notification-start.component';
import { FeedbackService, MsgType, VibrateType } from '../feedback.service';
import { GeneralService } from '../general.service';
import { StorageManagerService } from '../storage-manager.service';
import { HttpService } from '../http.service';
import { ChangeEvent, DeviceState, PermissionChange } from 'onesignal-cordova-plugin/dist/Subscription';
import dayjs from '../../_utils/dayjs.config';

const PUSH_WORKING_TIMES: string = "PUSH_WORKING_TIMES"

export interface OneSignalNotificationSettings {
  contentAdd: boolean;
  stateChange: boolean;
  allocationChange: boolean;
  desktopChange: boolean;
  appointmentChange: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  public activePushRoot: boolean = false

  public notificationSettings: OneSignalNotificationSettings = {
    contentAdd: false,
    stateChange: false,
    allocationChange: false,
    desktopChange: false,
    appointmentChange: false
  }

  public workingTimes: boolean = false

  private _notificationAppointment: BehaviorSubject<{date: Date, workstepId: number}> = new BehaviorSubject<{date: Date, workstepId: number}>({date: null, workstepId: -1})

  constructor(
    private feedback: FeedbackService,
    private storageManager: StorageManagerService,
    private generalSrv: GeneralService,
    private navCtrl: NavController,
    private http: HttpService,
    private platform: Platform
  ) {
    

  }

  public get calendarDateChanged(): Observable<{date: Date, workstepId: number}>{
    return this._notificationAppointment.asObservable()
  }


  public async toggleActivePushroot(){
    await this.feedback.vibrate(VibrateType.light )
    if(!this.activePushRoot) {
      OneSignal.disablePush(true)
      this.notificationSettings = { contentAdd: false, stateChange: false, allocationChange: false, desktopChange: false, appointmentChange: false }
      await this.saveUserSettings()
    }
    else await new Promise(resolve => {
        OneSignal.promptForPushNotificationsWithUserResponse(async (success)=>{
          if(success) {
            OneSignal.disablePush(false)
            this.notificationSettings = { contentAdd: true, stateChange: true, allocationChange: true, desktopChange: true, appointmentChange: true }
            await this.saveUserSettings()
          }
          else {
            this.activePushRoot = false
            this.feedback.showError("Benachrichtigungen konnten nicht aktiviert werden. Bitte prüfen Sie Ihre Einstellungen im Betriebssystem.")
          }
          resolve(true)
        })
      })
  } 


  /**
   * Speichert die Benachrichtigungseinstellungen des Benutzers
   */
  public async saveUserSettings(){
    await this.http.PUT({
      path: "onesignal/settings",
      body: this.notificationSettings
    }) 
  }

  /**
   * Lädt die Benachrichtigungseinstellungen des Benutzers
   */
  public async loadUserSettings(){

    //Ob Benachrichtigungen aktiviert sind
    OneSignal.getDeviceState((response: DeviceState) => {
      this.activePushRoot = !response.pushDisabled && response.hasNotificationPermission
    } )

    //Welche Benachrichtigungen aktiviert sind
    const result: HttpFunctionResult<OneSignalNotificationSettings> = await this.http.GET({
      path: "onesignal/settings" 
    }) 

    //Live Activities
    this.workingTimes = await this.storageManager.getItem<boolean>(PUSH_WORKING_TIMES, false) 

    if(result.success) this.notificationSettings = result.data
  }

  public async toggleWorkingTime(){
    await this.feedback.vibrate(VibrateType.light)
    await this.storageManager.setItem<boolean>(PUSH_WORKING_TIMES, this.workingTimes)
  }
  
  /**
   * Fragt beim ersten anmelden ab ob Benachrichtigungen aktiviert werden sollen
   * @param {boolean} isDemo bei true wird die frage nicht ausgelöst (für Demomodus) 
   */
  public async firstLoginNotificationRequest(isDemo: boolean){
    if(!this.platform.is('capacitor') || isDemo) return

    if(await this.storageManager.getItem<boolean>("FIRST_LOGIN_NOTIFICATION_REQUESTED")) return
    await this.storageManager.setItem<boolean>("FIRST_LOGIN_NOTIFICATION_REQUESTED", true)
    
    const notificationModalResponse: HttpFunctionResult<boolean> = await this.feedback.showModal<boolean>({
      component: NotificationStartComponent
    })

    if(!notificationModalResponse.success) return

    this.notificationSettings = { contentAdd: true, stateChange: true, allocationChange: true, desktopChange: true, appointmentChange: true }
    
    this.activePushRoot = true

    await Promise.all([
      this.saveUserSettings(),
      this.toggleActivePushroot()
    ])


  }

  public async subribeToNotificationHandler(tabs: IonTabs, userId: number){

    if(!this.platform.is("capacitor")) return
    OneSignal.setAppId("b28761b3-7618-41b1-80dc-24ea752f0c19")
    OneSignal.sendTag("userId", String(userId)) 
    if(!environment.production) OneSignal.sendTag("is_test", "true") 
    OneSignal.sendTag("companyIdentifier", await this.storageManager.getItem<string>("COMPANY_IDENTIFIER"))
    
    OneSignal.setNotificationOpenedHandler(async (data)=>{

      //0 = Allocation
      //1 = ContentAdd  
      //2 = StateChanged
      //3 = appointmentChange
      //4 = Ticketsystem Desktop
      const additionalData: any = (data.notification.additionalData as any )
      switch (additionalData.notificationReason as number) {
        case 0: case 4:
          await this.feedback.showLoading(async () => {
              this.generalSrv.setActiveTicket({id: additionalData.ticketId as number})
              this.feedback.showModal({
                component: TicketDetailPagePage,
                componentProps: { isModal: true, modelWorkstepId: additionalData.workstepId as number }
              })
          })
          break;
        case 1: 
          await this.feedback.showLoading(async () => {
            this.generalSrv.setActiveTicket({id: additionalData.ticketId as number})
            this.feedback.showModal({
              component: TicketDetailPagePage,
              componentProps: { isModal: true,  modelWorkstepId: additionalData.workstepId as number, highlightedContentId : additionalData.contentId as number }
            }) 
          })
          break;
        case 2:
          await this.feedback.showLoading(async () => {
            this.generalSrv.setActiveTicket({id: additionalData.ticketId as number})
            this.feedback.showModal({ component: TicketDetailPagePage, componentProps: {isModal: true} })
          })
          break
        case 3: 
          await this.feedback.showLoading(async () => {
            await this.navCtrl.navigateRoot(`/tabs/calendar-mobile/day/${dayjs(additionalData.time).format("YYYY-MM-DD")}`)
          })
          break
        default:
          break;
      }
    })
  }


}
